// DDA raycaster: textured walls + perspective floor/ceiling + depth-buffered
// billboard sprites, rendered into a small internal framebuffer (default
// 384x216) that is upscaled to the view. Small buffer = huge 60fps headroom,
// which is the whole point: the sim never starves and controls stay tight.

import { aUnpack, clamp, shadePixel } from "./math";
import { cellAt } from "./types";
import type { Level, SpriteFrame, TextureProvider } from "./types";

export interface Camera {
  x: number;
  y: number;
  angle: number;
}

export interface RenderSprite {
  x: number;
  y: number;
  frame: SpriteFrame;
  /** World height in cell units (1 ≈ full wall height). */
  worldScale: number;
  /** Extra distance-independent brightness (e.g. pain flash). 1 = normal. */
  bright?: number;
}

const PLANE_SCALE = 0.66; // ~66° FOV, Doom-like

function distanceShade(d: number): number {
  // Keep things readable (playability): gentle falloff, never fully black.
  return clamp(1.35 / (1 + d * 0.13), 0.16, 1.05);
}

export class Raycaster {
  readonly renderW: number;
  readonly renderH: number;
  private readonly buf: Uint32Array;
  private readonly image: ImageData;
  private readonly zbuf: Float32Array;
  private readonly off: HTMLCanvasElement;
  private readonly octx: CanvasRenderingContext2D;

  constructor(renderW = 384, renderH = 216) {
    this.renderW = renderW;
    this.renderH = renderH;
    // Back the framebuffer and the ImageData with one explicit ArrayBuffer so
    // the Uint32 view (fast pixel writes) and the Uint8Clamped view (putImageData)
    // alias the same memory and the ImageData overload resolves cleanly.
    const buffer = new ArrayBuffer(renderW * renderH * 4);
    this.buf = new Uint32Array(buffer);
    const bytes = new Uint8ClampedArray(buffer);
    this.image = new ImageData(bytes, renderW, renderH);
    this.zbuf = new Float32Array(renderW);
    this.off = document.createElement("canvas");
    this.off.width = renderW;
    this.off.height = renderH;
    const octx = this.off.getContext("2d", { alpha: false });
    if (!octx) throw new Error("offscreen 2d unavailable");
    octx.imageSmoothingEnabled = false;
    this.octx = octx;
  }

  /** Fill floor, ceiling and walls; populate the per-column depth buffer. */
  renderWorld(level: Level, textures: TextureProvider, cam: Camera, pitch = 0): void {
    const { renderW, renderH, buf, zbuf } = this;
    const dirX = Math.cos(cam.angle);
    const dirY = Math.sin(cam.angle);
    const planeX = -dirY * PLANE_SCALE;
    const planeY = dirX * PLANE_SCALE;
    const horizon = renderH * 0.5 + pitch;

    const floorTex = textures.get(level.floorTexId);
    const ceilTex = textures.get(level.ceilTexId);
    const fSize = floorTex.size;
    const fMask = fSize - 1;
    const cSize = ceilTex.size;
    const cMask = cSize - 1;

    // --- Floor + ceiling (row casting) ---
    const rayDirX0 = dirX - planeX;
    const rayDirY0 = dirY - planeY;
    const rayDirX1 = dirX + planeX;
    const rayDirY1 = dirY + planeY;
    const halfH = renderH * 0.5;

    // Floor: rows below the horizon.
    for (let y = Math.max(0, Math.floor(horizon) + 1); y < renderH; y++) {
      const p = y - horizon;
      if (p <= 0) continue;
      const rowDistance = halfH / p;
      const stepX = (rowDistance * (rayDirX1 - rayDirX0)) / renderW;
      const stepY = (rowDistance * (rayDirY1 - rayDirY0)) / renderW;
      let fx = cam.x + rowDistance * rayDirX0;
      let fy = cam.y + rowDistance * rayDirY0;
      const shade = distanceShade(rowDistance);
      let idx = y * renderW;
      for (let x = 0; x < renderW; x++) {
        const tx = (fx * fSize) & fMask;
        const ty = (fy * fSize) & fMask;
        buf[idx++] = shadePixel(floorTex.data[(ty * fSize + tx) >>> 0]!, shade);
        fx += stepX;
        fy += stepY;
      }
    }
    // Ceiling: rows above the horizon.
    for (let y = Math.min(renderH - 1, Math.ceil(horizon) - 1); y >= 0; y--) {
      const p = horizon - y;
      if (p <= 0) continue;
      const rowDistance = halfH / p;
      const stepX = (rowDistance * (rayDirX1 - rayDirX0)) / renderW;
      const stepY = (rowDistance * (rayDirY1 - rayDirY0)) / renderW;
      let fx = cam.x + rowDistance * rayDirX0;
      let fy = cam.y + rowDistance * rayDirY0;
      const shade = distanceShade(rowDistance) * 0.82;
      let idx = y * renderW;
      for (let x = 0; x < renderW; x++) {
        const tx = (fx * cSize) & cMask;
        const ty = (fy * cSize) & cMask;
        buf[idx++] = shadePixel(ceilTex.data[(ty * cSize + tx) >>> 0]!, shade);
        fx += stepX;
        fy += stepY;
      }
    }

    // --- Walls (DDA per column) ---
    for (let x = 0; x < renderW; x++) {
      const cameraX = (2 * x) / renderW - 1;
      const rayDirX = dirX + planeX * cameraX;
      const rayDirY = dirY + planeY * cameraX;

      let mapX = Math.floor(cam.x);
      let mapY = Math.floor(cam.y);
      const deltaDistX = rayDirX === 0 ? 1e30 : Math.abs(1 / rayDirX);
      const deltaDistY = rayDirY === 0 ? 1e30 : Math.abs(1 / rayDirY);

      let stepX: number;
      let stepY: number;
      let sideDistX: number;
      let sideDistY: number;
      if (rayDirX < 0) {
        stepX = -1;
        sideDistX = (cam.x - mapX) * deltaDistX;
      } else {
        stepX = 1;
        sideDistX = (mapX + 1 - cam.x) * deltaDistX;
      }
      if (rayDirY < 0) {
        stepY = -1;
        sideDistY = (cam.y - mapY) * deltaDistY;
      } else {
        stepY = 1;
        sideDistY = (mapY + 1 - cam.y) * deltaDistY;
      }

      let side = 0;
      let hit = 0;
      let guard = 0;
      while (guard++ < 512) {
        if (sideDistX < sideDistY) {
          sideDistX += deltaDistX;
          mapX += stepX;
          side = 0;
        } else {
          sideDistY += deltaDistY;
          mapY += stepY;
          side = 1;
        }
        hit = cellAt(level, mapX, mapY);
        if (hit !== 0) break;
      }

      const perpWallDist =
        side === 0 ? sideDistX - deltaDistX : sideDistY - deltaDistY;
      zbuf[x] = perpWallDist;

      const tex = textures.get(hit);
      const tSize = tex.size;
      let wallX =
        side === 0 ? cam.y + perpWallDist * rayDirY : cam.x + perpWallDist * rayDirX;
      wallX -= Math.floor(wallX);
      let texX = (wallX * tSize) | 0;
      if ((side === 0 && rayDirX > 0) || (side === 1 && rayDirY < 0)) {
        texX = tSize - texX - 1;
      }

      const lineHeight = renderH / perpWallDist;
      const drawStartF = -lineHeight / 2 + horizon;
      const drawStart = Math.max(0, Math.floor(drawStartF));
      const drawEnd = Math.min(renderH - 1, Math.floor(lineHeight / 2 + horizon));
      const shade = distanceShade(perpWallDist) * (side === 1 ? 0.7 : 1);

      const texStep = tSize / lineHeight;
      let texPos = (drawStart - drawStartF) * texStep;
      const col = texX >>> 0;
      for (let y = drawStart; y <= drawEnd; y++) {
        const ty = (texPos | 0) & (tSize - 1);
        texPos += texStep;
        buf[y * renderW + x] = shadePixel(tex.data[(ty * tSize + col) >>> 0]!, shade);
      }
    }
  }

  /** Composite depth-tested billboard sprites over the world (call after renderWorld). */
  drawSprites(sprites: readonly RenderSprite[], cam: Camera, pitch = 0): void {
    if (sprites.length === 0) return;
    const { renderW, renderH, buf, zbuf } = this;
    const dirX = Math.cos(cam.angle);
    const dirY = Math.sin(cam.angle);
    const planeX = -dirY * PLANE_SCALE;
    const planeY = dirX * PLANE_SCALE;
    const horizon = renderH * 0.5 + pitch;
    const halfH = renderH * 0.5;
    const invDet = 1 / (planeX * dirY - dirX * planeY);

    // Far → near so nearer sprites overwrite (alpha handled per pixel anyway).
    const order = sprites
      .map((s, i) => {
        const dx = s.x - cam.x;
        const dy = s.y - cam.y;
        return { i, d: dx * dx + dy * dy };
      })
      .sort((a, b) => b.d - a.d);

    for (const { i } of order) {
      const sp = sprites[i]!;
      const spriteX = sp.x - cam.x;
      const spriteY = sp.y - cam.y;
      const transformX = invDet * (dirY * spriteX - dirX * spriteY);
      const transformY = invDet * (-planeY * spriteX + planeX * spriteY); // depth
      if (transformY <= 0.05) continue;

      const screenX = Math.floor((renderW / 2) * (1 + transformX / transformY));
      const frame = sp.frame;
      const aspect = frame.width / frame.height;
      const drawH = Math.abs((renderH / transformY) * sp.worldScale);
      const drawW = drawH * aspect;
      // Feet on the floor at this depth.
      const feetY = horizon + halfH / transformY;
      const imageBottom = feetY - drawH * (1 - frame.anchorY);
      const imageTop = imageBottom - drawH;

      const startX = Math.max(0, Math.floor(screenX - (drawW * frame.anchorX)));
      const endX = Math.min(renderW - 1, Math.floor(screenX + drawW * (1 - frame.anchorX)));
      const startY = Math.max(0, Math.floor(imageTop));
      const endY = Math.min(renderH - 1, Math.floor(imageBottom));
      if (endX < startX || endY < startY) continue;

      const shade = distanceShade(transformY) * (sp.bright ?? 1);
      const fw = frame.width;
      const fh = frame.height;
      const data = frame.data;
      const spanX = drawW;
      const spanY = drawH;

      for (let x = startX; x <= endX; x++) {
        if (transformY >= zbuf[x]!) continue; // occluded by wall
        const texX = clamp(
          Math.floor(((x - (screenX - drawW * frame.anchorX)) / spanX) * fw),
          0,
          fw - 1,
        );
        for (let y = startY; y <= endY; y++) {
          const texY = clamp(Math.floor(((y - imageTop) / spanY) * fh), 0, fh - 1);
          const px = data[texY * fw + texX]!;
          const a = aUnpack(px);
          if (a === 0) continue;
          const shaded = shadePixel(px, shade);
          const dst = y * renderW + x;
          if (a >= 250) {
            buf[dst] = shaded;
          } else {
            // Source-over blend for soft edges.
            const inv = 255 - a;
            const d = buf[dst]!;
            const r = (((shaded & 0xff) * a + (d & 0xff) * inv) / 255) | 0;
            const g = ((((shaded >> 8) & 0xff) * a + ((d >> 8) & 0xff) * inv) / 255) | 0;
            const b = ((((shaded >> 16) & 0xff) * a + ((d >> 16) & 0xff) * inv) / 255) | 0;
            buf[dst] = ((0xff << 24) | (b << 16) | (g << 8) | r) >>> 0;
          }
        }
      }
    }
  }

  /** Blit the internal framebuffer to the destination context, scaled to the view. */
  present(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number): void {
    this.octx.putImageData(this.image, 0, 0);
    const prev = ctx.imageSmoothingEnabled;
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(this.off, 0, 0, this.renderW, this.renderH, x, y, w, h);
    ctx.imageSmoothingEnabled = prev;
  }
}
